import * as THREE from 'three';

import { buildBoneRefData } from './boneRefs';
import type { Skeleton } from './core';

type BoundThreeSkeletonBone = {
  targetIndex: number;
  sourceBone: THREE.Bone;
  /**
   * For source bones at the asset root (no Bone ancestor), source.local is
   * authored in the source asset's parent reference frame. When the loaded
   * character's matching parent reference frame differs (e.g. GLB whose
   * armature container has a rotation but the motion asset's container is
   * identity), we re-express source.local in the target's parent frame so
   * the resulting pose composes to the same world transform under the
   * character's actual scene parent. Null when no correction is required.
   */
  parentFrameCorrection: THREE.Matrix4 | null;
};

export type ThreeSkeletonPoseBinding = {
  bindings: BoundThreeSkeletonBone[];
  restInverseScratch: THREE.Matrix4;
  sourceLocalScratch: THREE.Matrix4;
  adjustedSourceLocalScratch: THREE.Matrix4;
};

const PARENT_FRAME_CORRECTION_EPSILON = 1e-6;

function isApproximatelyIdentityMatrix(matrix: THREE.Matrix4): boolean {
  const e = matrix.elements;
  return (
    Math.abs(e[0] - 1) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[5] - 1) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[10] - 1) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[15] - 1) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[1]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[2]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[3]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[4]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[6]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[7]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[8]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[9]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[11]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[12]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[13]) < PARENT_FRAME_CORRECTION_EPSILON &&
    Math.abs(e[14]) < PARENT_FRAME_CORRECTION_EPSILON
  );
}

function getParentBoneIndex(
  bone: THREE.Bone,
  indexByBone: Map<THREE.Bone, number>,
): number | null {
  let current: THREE.Object3D | null = bone.parent;
  while (current) {
    if (current instanceof THREE.Bone) {
      const parentIndex = indexByBone.get(current);
      if (parentIndex != null) return parentIndex;
    }
    current = current.parent;
  }
  return null;
}

function ensureOutputMatrices(
  output: THREE.Matrix4[] | undefined,
  count: number,
): THREE.Matrix4[] {
  const resolved = output ?? [];
  while (resolved.length < count) {
    resolved.push(new THREE.Matrix4());
  }
  return resolved;
}

export type CreateThreeSkeletonPoseBindingOptions = {
  /**
   * When true, asset-root source bones whose parent reference frame differs
   * from the matching target reference frame are pre-multiplied by a
   * one-time correction so source.local applies under the character's actual
   * scene parent.
   *
   * This is the correct interpretation for motion assets that carry a real
   * parent container (e.g. GLB motion files where the motion's armature
   * group has a known transform and the bones are authored relative to it).
   *
   * It is NOT correct for source bones whose "parent" is a synthetic
   * wrapper we build ourselves (e.g. the BVH root bones we re-parent into a
   * fresh `THREE.Group` for animation playback). In that case the BVH was
   * authored to apply directly under the target's parent and the parent is
   * not a meaningful frame, so we leave this off and the existing behaviour
   * is preserved. Defaults to false.
   */
  useSourceParentAsAuthoredFrame?: boolean;
};

export function createThreeSkeletonPoseBinding(
  sourceBones: THREE.Bone[],
  targetSkeleton: Skeleton,
  options: CreateThreeSkeletonPoseBindingOptions = {},
): ThreeSkeletonPoseBinding {
  const useSourceParentAsAuthoredFrame =
    options.useSourceParentAsAuthoredFrame ?? false;

  const indexByBone = new Map<THREE.Bone, number>();
  for (let index = 0; index < sourceBones.length; index++) {
    indexByBone.set(sourceBones[index], index);
  }

  const sourceRefs = buildBoneRefData(
    sourceBones.map((bone, index) => ({
      index,
      parentIndex: getParentBoneIndex(bone, indexByBone),
      name: bone.name,
    })),
  );

  const bindings: BoundThreeSkeletonBone[] = [];
  for (const targetBone of targetSkeleton.bones) {
    const targetRef = targetSkeleton.isAmbiguousName(targetBone.name)
      ? targetBone.internalFullPathRef
      : targetBone.name;
    const sourceIndex = sourceRefs.refToIndex.get(targetRef);
    if (sourceIndex == null) continue;

    const sourceBone = sourceBones[sourceIndex];
    if (!sourceBone) continue;

    let parentFrameCorrection: THREE.Matrix4 | null = null;
    if (useSourceParentAsAuthoredFrame) {
      const sourceHasBoneParent =
        getParentBoneIndex(sourceBone, indexByBone) !== null;
      if (!sourceHasBoneParent) {
        const sourceParentWorld = sourceBone.parent
          ? sourceBone.parent.matrixWorld.clone()
          : new THREE.Matrix4();
        const targetParentRestWorld =
          targetBone.parent === null
            ? targetSkeleton.rootParentWorld
            : targetSkeleton.bone(targetBone.parent).restWorld;
        const correction = new THREE.Matrix4()
          .copy(targetParentRestWorld)
          .invert()
          .multiply(sourceParentWorld);
        if (!isApproximatelyIdentityMatrix(correction)) {
          parentFrameCorrection = correction;
        }
      }
    }

    bindings.push({
      targetIndex: targetBone.index,
      sourceBone,
      parentFrameCorrection,
    });
  }

  return {
    bindings,
    restInverseScratch: new THREE.Matrix4(),
    sourceLocalScratch: new THREE.Matrix4(),
    adjustedSourceLocalScratch: new THREE.Matrix4(),
  };
}

export function sampleThreeSkeletonPoseLocals(
  binding: ThreeSkeletonPoseBinding,
  targetSkeleton: Skeleton,
  output?: THREE.Matrix4[],
): THREE.Matrix4[] {
  const poseLocals = ensureOutputMatrices(output, targetSkeleton.bones.length);
  for (let index = 0; index < targetSkeleton.bones.length; index++) {
    poseLocals[index].identity();
  }

  const sourceLocal = binding.sourceLocalScratch;
  const adjustedSourceLocal = binding.adjustedSourceLocalScratch;
  const restInverse = binding.restInverseScratch;

  for (const {
    targetIndex,
    sourceBone,
    parentFrameCorrection,
  } of binding.bindings) {
    const targetBone = targetSkeleton.bone(targetIndex);
    sourceLocal.compose(
      sourceBone.position,
      sourceBone.quaternion,
      sourceBone.scale,
    );
    const effectiveSourceLocal = parentFrameCorrection
      ? adjustedSourceLocal
          .copy(parentFrameCorrection)
          .multiply(sourceLocal)
      : sourceLocal;
    restInverse.copy(targetBone.restLocal).invert();
    poseLocals[targetIndex].copy(restInverse).multiply(effectiveSourceLocal);
  }

  return poseLocals;
}
